// ClientDashboard.jsx (Axios + React Router v5 param `clientId`)
// Fluent UI v8 (plain JS)
// Shows client overview + associated Projects and Proposals.
//
// API endpoints (adjust if yours differ):
//   GET /api/clients/:clientId
//   GET /api/clients/:clientId/projects
//   GET /api/clients/:clientId/proposals

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Stack,
  Text,
  Separator,
  // CommandBar,
  DetailsList,
  DetailsListLayoutMode,
  SelectionMode,
  SearchBox,
  Spinner,
  MessageBar,
  MessageBarType,
  Link as BaseLink,
  Depths,
} from '@fluentui/react';
import { mergeStyleSets } from '@fluentui/react';
import MetaTable from '../components/MetaTable';

// Optional: centralize Axios config
const api = axios.create({ baseURL: '/api' });

const styles = mergeStyleSets({
  page: { maxWidth: 1200, margin: '24px auto', padding: 16 },
  card: { 
    background: 'white',
    border: '1px solid #e1dfdd',
    borderRadius: 8,
    padding: 16,
    boxShadow: Depths.depth8
   },
});

export default function ClientDashboard() {
  // IMPORTANT: param is clientId
  const { clientId } = useParams();

  const [client, setClient] = useState(null);
  const [projects, setProjects] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [qProjects, setQProjects] = useState('');
  const [qProposals, setQProposals] = useState('');

  const fetchAll = useCallback(async () => {
    setError(''); setLoading(true);
    try {
      const [cRes, prjRes, propRes] = await Promise.all([
        api.get(`/clients/${clientId}`),
        api.get(`/clients/${clientId}/projects`).catch(() => ({ data: [] })),
        api.get(`/clients/${clientId}/proposals`).catch(() => ({ data: [] })),
      ]);

      setClient(cRes.data);
      setProjects(Array.isArray(prjRes.data) ? prjRes.data : []);
      setProposals(Array.isArray(propRes.data) ? propRes.data : []);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.error || e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { if (clientId) fetchAll(); }, [fetchAll, clientId]);

  const metaItems = useMemo(() => {
    if (!client) return [];
    return [
      { label: 'Client', text: client.name || client.client_name || '' },
      { label: 'Sector', text: client.sector || '' },
      { label: 'Region', text: client.region || client.location || '' },
      { label: 'Website', text: client.website_url || '', onRender: () => <BaseLink href={client.website_url}>{client.website_url}</BaseLink> },
    ];
  }, [client]);

  const filteredProjects = useMemo(() => {
    const q = qProjects.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter(p => (
      (p.title || '').toLowerCase().includes(q) ||
      (p.project_number || '').toLowerCase().includes(q) ||
      (p.status || '').toLowerCase().includes(q)
    ));
  }, [projects, qProjects]);

  const filteredProposals = useMemo(() => {
    const q = qProposals.trim().toLowerCase();
    if (!q) return proposals;
    return proposals.filter(p => (
      (p.title || '').toLowerCase().includes(q) ||
      (p.proposal_number || '').toLowerCase().includes(q) ||
      (p.status || '').toLowerCase().includes(q)
    ));
  }, [proposals, qProposals]);

  const projectColumns = [
    { key: 'title', name: 'Title', fieldName: 'title', minWidth: 220, isResizable: true, onRender: (item) => (
        <Link to={`/projects/${item.id}`}>{item.title}</Link>
      )
    },
    { key: 'pn', name: '#', fieldName: 'project_number', minWidth: 80, maxWidth: 120 },
    { key: 'status', name: 'Status', fieldName: 'status', minWidth: 80, maxWidth: 120 },
    { key: 'dates', name: 'Dates', minWidth: 160, onRender: (it) => (
        <span>{(it.summary_start_date || '')}{it.summary_end_date ? ` – ${it.summary_end_date}` : ''}</span>
      ) },
  ];

  const proposalColumns = [
    // { key: 'ppn', name: '#', fieldName: 'proposal_number', minWidth: 80, maxWidth: 120 },
    { key: 'title', name: 'Title', fieldName: 'title', minWidth: 220, isResizable: true, onRender: (item) => (
        <Link to={`/proposals/${item.id}`}>{item.title}</Link>
      ) },
    { key: 'status', name: 'Status', fieldName: 'status', minWidth: 80, maxWidth: 120 },
  ];

  return (
    <div className={styles.page}>
      {loading && (
        <Stack tokens={{ childrenGap: 8 }}>
          <Spinner label="Loading client" />
        </Stack>
      )}

      {!loading && error && (
        <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>
      )}

      {!loading && !error && client && (
        <Stack tokens={{childrenGap: 16}}>
          <Stack horizontal verticalAlign="center" horizontalAlign="space-between">
            <Stack>
              <Text variant="xxLarge">{client.name || client.client_name}</Text>
            </Stack>
          </Stack>

          {/* Overview */}
          <div className={styles.card}>
            <Text variant="large" styles={{ root: { fontWeight: 600 } }}>Overview</Text>
            <MetaTable borderless items={metaItems} labelColWidth="120px" />
          </div>

          {/* Projects */}
          <Stack tokens={{ childrenGap: 8 }}>
            <div className={styles.card}>
              <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                <Text variant="large" styles={{ root: { fontWeight: 600 } }}>Projects</Text>
                <SearchBox placeholder="Filter projects" value={qProjects} onChange={(_, v) => setQProjects(v || '')} styles={{ root: { maxWidth: 260 }}} />
              </Stack>
              <Separator />
              <DetailsList items={filteredProjects} columns={projectColumns} layoutMode={DetailsListLayoutMode.justified} selectionMode={SelectionMode.none} compact />
            </div>
          </Stack>

          {/* Proposals */}
          <Stack tokens={{ childrenGap: 8 }}>
            <div className={styles.card}>
              <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                <Text variant="large" styles={{ root: { fontWeight: 600 } }}>Proposals</Text>
                <SearchBox placeholder="Filter proposals" value={qProposals} onChange={(_, v) => setQProposals(v || '')} styles={{ root: { maxWidth: 260 }}} />
              </Stack>
              <Separator />
              <DetailsList 
                items={filteredProposals} 
                columns={proposalColumns} 
                layoutMode={DetailsListLayoutMode.justified} 
                selectionMode={SelectionMode.none} compact />
            </div>
          </Stack>
        </Stack>
      )}
    </div>
  );
}